import type { CreateTaskInput, Task, TaskColumn, UpdateTaskInput } from '@nexo/types';
import { useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { keys } from '../../lib/queries';
import { errorMessage } from '../../lib/forms';

export function useCreateTask() {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.tasks.create(team.id, input),
    onSuccess: (task) => cache.upsertTask(queryClient, task),
    onError: (err) =>
      toast({
        title: t('No se pudo crear la tarea'),
        description: errorMessage(err),
        tone: 'error',
      }),
  });
}

/** Optimistic update: the UI changes instantly and rolls back on error. */
export function useUpdateTask() {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTaskInput }) =>
      api.tasks.update(id, input),
    onMutate: ({ id, input }) => {
      const previous = queryClient.getQueryData<Task[]>(keys.tasks(team.id));
      const current = previous?.find((task) => task.id === id);
      if (current) {
        const { assigneeIds: _ignored, ...fields } = input;
        // A column carries its status category; a status alone moves the task out of its column.
        const columns = queryClient.getQueryData<TaskColumn[]>(keys.columns(team.id)) ?? [];
        const column = columns.find((c) => c.id === input.columnId);
        cache.upsertTask(queryClient, {
          ...current,
          ...fields,
          ...(column && { status: column.category }),
          ...(input.status && !column && input.status !== current.status && { columnId: null }),
        });
      }
      return { previous };
    },
    onSuccess: (task) => cache.upsertTask(queryClient, task),
    onError: (err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(keys.tasks(team.id), context.previous);
      toast({
        title: t('No se pudo actualizar la tarea'),
        description: errorMessage(err),
        tone: 'error',
      });
    },
  });
}

export function useDeleteTask() {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => api.tasks.remove(id),
    onSuccess: (_data, id) => cache.removeTask(queryClient, team.id, id),
    onError: (err) =>
      toast({
        title: t('No se pudo borrar la tarea'),
        description: errorMessage(err),
        tone: 'error',
      }),
  });
}
